import {useEffect, useState, useMemo, useCallback, useRef} from 'react';
import {useRouteMatch} from 'react-router-dom';

import {DocumentOperateData, DocumentOperateType, DocumentUpdateOperation} from '@/interface';
import sdk from '@/sdk';
import * as store from '@/store';
import {PageScope} from '@/Constant';
import routers, {buildPath} from '@/config/routers';
import eventBus, {ADD_COMMON_DOC_SUCCESS, REMOVE_COMMON_DOC_SUCCESS} from '@/eventBus';
import {isPersonalMyself} from '@/domains/space/Repository/knowledgeUtils';
import {RootNodeId} from '@/domains/space/Repository/SidebarNew/type';

import Node from './Node';
import Drag from './Drag';

import nodeStyles from './Node/index.module.scss';
import styles from './index.module.scss';

const positionSpace = 65536;

interface NodeData {
    id: string;
    title: string;
    url: string;
    isSelected?: boolean;
    docData: WizDocument;
}

interface IProps {
    onLoad?: (type: string) => void;
    documentOperate?: DocumentOperateData;
    sidebarLoad: boolean;
}

const CommonDocComp: React.FC<IProps> = ({documentOperate = {}, onLoad}) => {
    const match = useRouteMatch<any>(routers.Knowledge.docPath);
    const docGuid = match?.params?.docGuid;
    const [renameMode, setRenameMode] = useState(false);

    function sort(list: WizDocument[]) {
        return list.sort((a, b) => {
            return (b.position || 0) - (a.position || 0);
        });
    }

    const [docs, setDocs] = useState<WizDocument[]>([]);
    // 记录doc更新依赖的操作
    const updateDocType = useRef('');
    const getDocs = useCallback(async () => {
        const repository: Repository = store.getData(store.KEYS.CUR_REPOSITORY);
        const isMyPersonalRepo = isPersonalMyself(repository, sdk.user?.userGuid!);
        if (repository && isMyPersonalRepo) {
            const ksServer = await sdk.getKsServer(repository);
            return ksServer.getCommonDocs(repository.repositoryGuid).then((result: WizDocument[]) => {
                if (result) {
                    updateDocType.current = 'firstLoad';
                    setDocs(sort(result));
                }
            });
        }
        return null;
    }, []);

    useEffect(() => {
        getDocs();
    }, [getDocs]);

    useEffect(() => {
        eventBus.on(ADD_COMMON_DOC_SUCCESS, getDocs);
        eventBus.on(REMOVE_COMMON_DOC_SUCCESS, getDocs);

        return () => {
            eventBus.off(ADD_COMMON_DOC_SUCCESS, getDocs);
            eventBus.off(REMOVE_COMMON_DOC_SUCCESS, getDocs);
        };
    });

    // 用户操作页面后，更新文档信息
    useEffect(() => {
        const {docList = []} = documentOperate;

        if (
            [DocumentOperateType.Delete, DocumentOperateType.Across].includes(
                documentOperate.type as DocumentOperateType
            ) || [DocumentOperateType.Delete, DocumentOperateType.Across].includes(
                documentOperate.updateType as DocumentOperateType
            )
        ) {
            getDocs();
        } else if (documentOperate.type === DocumentOperateType.Update) {
            docList.forEach((item: any) => {
                if (item.action === DocumentOperateType.Rename) {
                    docs.some(doc => {
                        if (doc.docGuid === item.docGuid) {
                            doc.name = item.name;
                            return true;
                        }

                        return false;
                    });
                } else if (item.action === DocumentOperateType.Knowledge) {
                    let scope = PageScope.PagePublic;
                    if (item.operation === DocumentUpdateOperation.Add) {
                        scope = PageScope.PagePublic;
                    } else if (item.operation === DocumentUpdateOperation.Delete) {
                        scope = PageScope.pageClosed;
                    }

                    docs.some(doc => {
                        if (doc.docGuid === item.docGuid) {
                            doc.scope = scope;
                            return true;
                        }

                        return false;
                    });
                }
            });

            setDocs([...docs]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentOperate]);

    const movePos = useCallback(
        async (index: number, dropIndex: number) => {
            if (index !== dropIndex || index !== dropIndex - 1) {
                const doc = docs[index];
                let position = 0;
                if (dropIndex === 0) {
                    position = (docs[0].position || Date.now()) + positionSpace;
                } else if (dropIndex === docs.length) {
                    position = (docs[docs.length - 1].position || 0) / 2;
                } else {
                    position = (docs[dropIndex - 1].position! + docs[dropIndex].position!) / 2;
                }

                const repository: Repository = store.getData(store.KEYS.CUR_REPOSITORY);
                if (repository) {
                    const ksServer = await sdk.getKsServer(repository);
                    ksServer.moveCommonDoc(doc.docGuid, position, repository.repositoryGuid).then(() => {
                        doc.position = position;
                        setDocs([...sort(docs)]);
                    });
                }
            }
        },
        [docs]
    );

    useEffect(() => {
        store.setCommonDocs(docs);
        if (updateDocType.current === 'firstLoad') {
            onLoad && onLoad(RootNodeId.CommonDoc);
            updateDocType.current = 'unKnow';
        }
        return () => {
            store.setCommonDocs([]);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docs]);

    const nodes = useMemo(() => {
        const nodes = docs.map(doc => {
            const node: NodeData = {
                id: doc.docGuid,
                title: doc.name || '',
                url: buildPath(routers.Knowledge.path, {
                    spaceGuid: doc.spaceGuid || '',
                    groupGuid: doc.groupGuid || '',
                    repositoryGuid: doc.repositoryGuid || '',
                    docGuid: doc.docGuid || '',
                }),
                isSelected: doc.docGuid === docGuid,
                docData: doc,
            };
            return node;
        });

        return nodes;
    }, [docs, docGuid]);

    if (docs.length === 0) {
        return (
            <div className={styles.noData}>
                <div className={styles.content}>可在目录树右侧的“...”中添加常用页面</div>
            </div>
        );
    }

    return (
        <Drag.Context>
            <div>
                {nodes.map((item, index) => {
                    const onDrop = (dropIndex: number) => {
                        movePos(index, dropIndex);
                    };
                    return (
                        <Drag
                            key={item.id}
                            index={index}
                            onDrop={onDrop}
                            disabled={renameMode}
                            dragShadowWrapClassName={nodeStyles.dragShadowWrap}
                        >
                            <Node
                                title={item.title}
                                url={item.url}
                                isSelected={item.isSelected}
                                docData={item.docData}
                                setRenameMode={setRenameMode}
                            />
                        </Drag>
                    );
                })}
            </div>
        </Drag.Context>
    );
};

const CommonDoc = store.connect([store.KEYS.DOCUMENT_OPERATE])(CommonDocComp);

export default CommonDoc;
